import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { leadService } from '../../services/leadService';
import { campaignService } from '../../services/campaignService';
import { agentService } from '../../services/agentService';
import { Lead, LeadStatus, LeadSubStage } from '../../types/lead';
import { Campaign } from '../../types/campaign';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import SectionHeader from '../../components/UI/SectionHeader';
import Button from '../../components/UI/Button';
import LeadDetailSidebar from '../../components/Leads/LeadDetailSidebar';
import LeadResultCard from '../../components/Leads/LeadResultCard';
import SearchableSelect from '../../components/UI/SearchableSelect';
import { useAuth } from '../../contexts/AuthContext';
import { isViewResultsDialableCampaign, useDialLead } from '../../hooks/useDialLead';
import { useWebRTC } from '../../contexts/WebRTCContext';
import FollowUpDateRangeFilter, { FollowUpDateRange } from '../../components/Leads/FollowUpDateRangeFilter';
import toast from 'react-hot-toast';

type PipelineTab = 'all' | 'interested' | 'follow_up' | 'app_out' | 'app_in';

const PAGE_SIZE = 25;

const PIPELINE_TABS: { id: PipelineTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'interested', label: 'Interested' },
  { id: 'follow_up', label: 'Follow Up' },
  { id: 'app_out', label: 'App Out' },
  { id: 'app_in', label: 'App In' },
];

const PIPELINE_STATUSES: LeadStatus[] = ['interested', 'follow_up', 'app_out', 'app_in'];

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'app_out', label: 'App Out' },
  { value: 'app_in', label: 'App In' },
  { value: 'unqualified', label: 'Unqualified' },
];

function resolveStatusFilter(activeTab: PipelineTab): LeadStatus[] {
  if (activeTab === 'all') return PIPELINE_STATUSES;
  return [activeTab];
}

const GlobalResultsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<PipelineTab>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [datePreset, setDatePreset] = useState<'today' | 'yesterday' | 'all' | 'custom'>('all');
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  const [showCustom, setShowCustom] = useState(false);
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [subStageCounts, setSubStageCounts] = useState<Record<string, number>>({});
  const [campaignFilter, setCampaignFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [subStageFilter, setSubStageFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [followUpDateRange, setFollowUpDateRange] = useState<FollowUpDateRange | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [agents, setAgents] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [subStages, setSubStages] = useState<LeadSubStage[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [updatingLeadId, setUpdatingLeadId] = useState<string | null>(null);
  const [subStageCache, setSubStageCache] = useState<Record<string, LeadSubStage[]>>({});
  const loadedSubStageKeys = useRef(new Set<string>());
  const { dialLead, isDialing } = useDialLead();
  const { setOnDispositionComplete } = useWebRTC();
  const { user, isAdmin } = useAuth();
  const isPlatformAdmin = user?.role === 'admin';
  const campaignMap = useMemo(() => new Map(campaigns.map((c) => [c.id, c])), [campaigns]);

  // Today / Yesterday / All / Custom quick date filter — filters by last status-change date.
  const dateRange = useMemo(() => {
    if (datePreset === 'all') return null;
    if (datePreset === 'custom') return customRange;
    const pad = (n: number) => String(n).padStart(2, '0');
    const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const now = new Date();
    if (datePreset === 'today') {
      const d = ymd(now);
      return { from: d, to: d };
    }
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const d = ymd(yesterday);
    return { from: d, to: d };
  }, [datePreset, customRange]);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const response = isPlatformAdmin
          ? await campaignService.getAllCampaignsForAdmin({ limit: 1000 })
          : await campaignService.getCampaigns({ limit: 1000 });
        setCampaigns(response.campaigns);
      } catch {
        setCampaigns([]);
      }
    };
    void loadCampaigns();
  }, [isPlatformAdmin]);

  useEffect(() => {
    agentService.getTagOptions().then(setAgents).catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const activeMainStatus = activeTab === 'all' ? undefined : activeTab;

  // Sub-stages appear as soon as a status tab is picked. Scoped to the selected campaign
  // when one is chosen, otherwise owner-scoped by main status (works across all campaigns).
  useEffect(() => {
    const loadSubStages = async () => {
      if (!activeMainStatus) {
        setSubStages([]);
        setSubStageFilter('');
        return;
      }
      try {
        const stages = await leadService.getSubStages({
          campaignId: campaignFilter || undefined,
          mainStatus: activeMainStatus,
        });
        setSubStages(stages);
      } catch {
        setSubStages([]);
      }
    };
    void loadSubStages();
  }, [campaignFilter, activeMainStatus]);

  // Tab + sub-stage counts (respects campaign/agent/search/date filters).
  const fetchCounts = useCallback(async () => {
    try {
      const counts = await leadService.getLeadResultCounts({
        status: activeMainStatus ? [activeMainStatus as LeadStatus] : undefined,
        search: debouncedSearch || undefined,
        campaignId: campaignFilter || undefined,
        agentId: agentFilter || undefined,
        dateFrom: dateRange?.from,
        dateTo: dateRange?.to,
      });
      setTabCounts(counts.statusCounts || {});
      setSubStageCounts(counts.subStageCounts || {});
    } catch {
      /* non-fatal */
    }
  }, [activeMainStatus, debouncedSearch, campaignFilter, agentFilter, dateRange]);

  useEffect(() => {
    void fetchCounts();
  }, [fetchCounts]);

  const fetchLeads = useCallback(async (page = 1, opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true);
      const output = await leadService.getAllLeads({
        status: followUpDateRange ? ['follow_up'] : resolveStatusFilter(activeTab),
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        campaignId: campaignFilter || undefined,
        subStageId: subStageFilter || undefined,
        agentId: agentFilter || undefined,
        followUpDateFrom: followUpDateRange?.from,
        followUpDateTo: followUpDateRange?.to,
        dateFrom: dateRange?.from,
        dateTo: dateRange?.to,
        sortBy: 'statusChangedAt',
        sortOrder,
      });
      setLeads(output.data);
      setTotalCount(Number(output.pagination.total) || 0);
      setTotalPages(Number(output.pagination.totalPages) || 1);
      setCurrentPage(Number(output.pagination.page) || 1);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [activeTab, debouncedSearch, campaignFilter, subStageFilter, agentFilter, followUpDateRange, sortOrder, dateRange]);

  useEffect(() => {
    setOnDispositionComplete(() => {
      void fetchLeads(currentPage, { silent: true });
      void fetchCounts();
    });
    return () => setOnDispositionComplete(null);
  }, [currentPage, fetchLeads, fetchCounts, setOnDispositionComplete]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLeads(1);
  }, [fetchLeads]);

  // Load sub-stage dropdown options once per campaign (all statuses in one request),
  // then group by main status — avoids a request per (campaign × status) combination.
  useEffect(() => {
    const pendingCampaigns = new Set<string>();
    for (const lead of leads) {
      if (lead.campaignId && !loadedSubStageKeys.current.has(lead.campaignId)) {
        pendingCampaigns.add(lead.campaignId);
      }
    }
    for (const cid of pendingCampaigns) {
      loadedSubStageKeys.current.add(cid);
      leadService.getSubStages({ campaignId: cid })
        .then((stages) => setSubStageCache((prev) => {
          const next = { ...prev };
          const byStatus: Record<string, LeadSubStage[]> = {};
          for (const s of stages) (byStatus[s.mainStatus] ??= []).push(s);
          for (const [st, list] of Object.entries(byStatus)) next[`${cid}:${st}`] = list;
          return next;
        }))
        .catch(() => undefined);
    }
  }, [leads]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    fetchLeads(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const campaignOptions = useMemo(
    () =>
      campaigns
        .filter((c) => c.status !== 'trash')
        .map((c) => ({
          value: c.id,
          label: c.status === 'active' ? c.name : `${c.name} (${c.status})`,
        })),
    [campaigns]
  );

  const agentOptions = useMemo(
    () => agents.map((a) => ({ value: a.id, label: a.name, keywords: a.email })),
    [agents]
  );

  const handleDelete = async (leadId: string) => {
    if (!window.confirm('Move this lead to trash? You can restore it later.')) return;
    try {
      await leadService.deleteLead(leadId);
      toast.success('Lead moved to trash');
      await fetchLeads(currentPage, { silent: true });
      void fetchCounts();
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const handleLeadUpdate = async (lead: Lead, patch: { status?: LeadStatus; subStageId?: string | null }, e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    setUpdatingLeadId(lead.id);
    try {
      await leadService.updateLead(lead.id, patch);
      const pipelineTabs: PipelineTab[] = ['interested', 'follow_up', 'app_out', 'app_in'];
      if (patch.status && pipelineTabs.includes(patch.status as PipelineTab)) {
        setActiveTab(patch.status as PipelineTab);
        setCurrentPage(1);
        await fetchLeads(1, { silent: true });
      } else {
        await fetchLeads(currentPage, { silent: true });
      }
      void fetchCounts();
    } catch {
      toast.error('Failed to update lead');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader title="All Results" description="View outcomes across all campaigns with timeline, notes, and call history." />

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[200px]">
            <SearchableSelect
              id="global-campaign-filter"
              label="Campaign"
              value={campaignFilter}
              onChange={(v) => { setCampaignFilter(v); setCurrentPage(1); setSubStageFilter(''); }}
              options={campaignOptions}
              allOption={{ value: '', label: 'All campaigns' }}
              searchPlaceholder="Search campaigns…"
              emptyMessage="No campaigns found"
            />
          </div>

          <div className="min-w-[180px]">
            <SearchableSelect
              id="global-agent-filter"
              label="Filter by agent"
              value={agentFilter}
              onChange={(v) => { setAgentFilter(v); setCurrentPage(1); }}
              options={agentOptions}
              allOption={{ value: '', label: 'All agents' }}
              searchPlaceholder="Search agents…"
              emptyMessage="No agents found"
            />
          </div>

          <div className="flex-1 min-w-[220px] max-w-md">
            <label htmlFor="global-search" className="block text-xs font-medium text-muted-foreground mb-1">
              Search
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                id="global-search"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name, phone, company..."
                className="input w-full pl-9 text-sm"
              />
            </div>
          </div>

          <div className="min-w-[160px]">
            <label htmlFor="global-sort" className="block text-xs font-medium text-muted-foreground mb-1">
              Sort by status change
            </label>
            <select
              id="global-sort"
              className="block h-9 w-full min-w-[10rem] rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={sortOrder}
              onChange={(e) => { setSortOrder(e.target.value as 'desc' | 'asc'); setCurrentPage(1); }}
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center rounded-lg border border-border overflow-hidden w-fit">
          {(['today', 'yesterday', 'all', 'custom'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setDatePreset(p);
                setCurrentPage(1);
                if (p === 'custom') {
                  setDraftFrom(customRange?.from ?? '');
                  setDraftTo(customRange?.to ?? '');
                  setShowCustom(true);
                } else {
                  setShowCustom(false);
                }
              }}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                datePreset === p ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted/40'
              } ${p !== 'today' ? 'border-l border-border' : ''}`}
            >
              {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'all' ? 'All Time' : 'Custom'}
            </button>
          ))}
        </div>
        {datePreset === 'custom' && showCustom && (
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card px-3 py-2">
            <label className="text-xs text-muted-foreground">
              From
              <input
                type="date"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="mt-1 block rounded-lg border border-border bg-background px-2 py-1 text-sm"
              />
            </label>
            <label className="text-xs text-muted-foreground">
              To
              <input
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => setDraftTo(e.target.value)}
                className="mt-1 block rounded-lg border border-border bg-background px-2 py-1 text-sm"
              />
            </label>
            <Button
              size="sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo}
              onClick={() => {
                setCustomRange({ from: draftFrom, to: draftTo });
                setShowCustom(false);
                setCurrentPage(1);
              }}
            >
              Apply
            </Button>
          </div>
        )}
        {datePreset === 'custom' && customRange && !showCustom && (
          <span className="text-xs text-muted-foreground">
            {customRange.from} → {customRange.to}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {PIPELINE_TABS.map((tab) => {
          const count = tab.id === 'all'
            ? PIPELINE_STATUSES.reduce((sum, st) => sum + (tabCounts[st] || 0), 0)
            : (tabCounts[tab.id] || 0);
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => { setActiveTab(tab.id); setCurrentPage(1); setSubStageFilter(''); }}
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border ${
                isActive ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'
              }`}
            >
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-muted-foreground/10'}`}>
                {count}
              </span>
            </button>
          );
        })}
        <FollowUpDateRangeFilter
          value={followUpDateRange}
          onChange={(range) => {
            setFollowUpDateRange(range);
            if (range) {
              setActiveTab('follow_up');
            }
            setCurrentPage(1);
            setSubStageFilter('');
          }}
        />
      </div>

      {subStages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { setSubStageFilter(''); setCurrentPage(1); }}
            className={`text-xs px-3 py-1 rounded-full border ${!subStageFilter ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}
          >
            All sub-stages
          </button>
          {subStages.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { setSubStageFilter(s.id); setCurrentPage(1); }}
              className={`text-xs px-3 py-1 rounded-full border ${subStageFilter === s.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}
            >
              {s.name} ({subStageCounts[s.id] || 0})
            </button>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Results ({totalCount.toLocaleString()})</CardTitle>
            {totalPages > 1 && <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><LoadingSpinner /></div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No leads found.</div>
          ) : (
            <>
              <div className="space-y-4">
                {leads.map((lead) => (
                  <LeadResultCard
                    key={lead.id}
                    lead={lead}
                    statusOptions={STATUS_OPTIONS}
                    subStageOptions={subStageCache[`${lead.campaignId}:${lead.status}`] || []}
                    updating={updatingLeadId === lead.id}
                    showCampaign
                    enableContactActions
                    dialing={isDialing(lead.id)}
                    onDial={
                      lead.campaignId && isViewResultsDialableCampaign(campaignMap.get(lead.campaignId))
                        ? (e) => {
                            e.stopPropagation();
                            void dialLead(lead.id, lead.campaignId!, lead.fullName, {
                              resultsCallback: true,
                            });
                          }
                        : undefined
                    }
                    onLeadFieldUpdated={() => fetchLeads(currentPage, { silent: true })}
                    onDelete={isAdmin ? () => handleDelete(lead.id) : undefined}
                    onViewDetails={() => setSelectedLeadId(lead.id)}
                    onStatusChange={(status, e) => handleLeadUpdate(lead, { status }, e)}
                    onSubStageChange={(subStageId, e) => handleLeadUpdate(lead, { subStageId }, e)}
                    onTagsUpdated={() => fetchLeads(currentPage, { silent: true })}
                    onFollowUpUpdated={() => fetchLeads(currentPage, { silent: true })}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex justify-between mt-6 pt-4 border-t border-border">
                  <Button variant="outlined" size="sm" disabled={currentPage <= 1} onClick={() => handlePageChange(currentPage - 1)}>
                    <ChevronLeftIcon className="h-4 w-4 mr-1" /> Previous
                  </Button>
                  <Button variant="outlined" size="sm" disabled={currentPage >= totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                    Next <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <LeadDetailSidebar
        leadId={selectedLeadId}
        enableContactActions
        onClose={() => setSelectedLeadId(null)}
        onLeadUpdated={() => fetchLeads(currentPage, { silent: true })}
      />
    </div>
  );
};

export default GlobalResultsPage;
